/**
 * Prezentacja stanu zlecenia na osi „od przyjęcia do zapisu w TMS klienta".
 *
 * **Źródłem prawdy dla samego zestawu stanów jest backend**:
 * `apps/api/src/user-offers/acceptance-status.ts`. Tutaj są wyłącznie etykiety
 * i kolory, czyli rzeczy, których backend nie potrzebuje.
 *
 * ## Dlaczego to nie jest import, tylko druga kopia
 *
 * Monorepo ma dziś wyłącznie `apps/*`, bez wspólnego pakietu, a CRA nie
 * kompiluje modułów spoza `src`. Wciągnięcie wspólnego pakietu to osobna
 * zmiana w budowaniu, której nie robimy w środku portu.
 *
 * **To właśnie brak takiego miejsca był przyczyną błędu, który tu naprawiamy** —
 * w FreightFusion lista i widok szczegółów miały każde swoją listę stanów
 * i się rozjechały. Dlatego zgodności pilnuje test
 * (`statusZlecenia.test.ts`), który czyta plik backendu i porównuje zestawy.
 * Rozjazd = czerwony test, a nie cicha rozbieżność w interfejsie.
 */

export const STATUS_NEW = 'new';
export const STATUS_ACCEPTED = 'accepted';
export const STATUS_SAVE_TO_TMS = 'save_to_tms';
export const STATUS_IN_TMS = 'in_tms';
export const STATUS_FAILED_PUSH_TMS = 'failed_push_tms';

export const ALL_ACCEPTANCE_STATUSES = [
  STATUS_NEW,
  STATUS_ACCEPTED,
  STATUS_SAVE_TO_TMS,
  STATUS_IN_TMS,
  STATUS_FAILED_PUSH_TMS,
] as const;

export type AcceptanceStatus = (typeof ALL_ACCEPTANCE_STATUSES)[number];

export type AcceptanceStatusAppearance = {
  label: string;
  textColor: string;
  backgroundColor: string;
};

/**
 * Wygląd stanu. Kolory przeniesione z listy zleceń FreightFusion, żeby
 * operator nie musiał się przyzwyczajać od nowa.
 */
export const STATUS_APPEARANCE: Record<AcceptanceStatus, AcceptanceStatusAppearance> = {
  [STATUS_NEW]: {
    label: 'Nowe',
    textColor: '#6b7280',
    backgroundColor: '#f3f4f6',
  },
  [STATUS_ACCEPTED]: {
    label: 'Zaakceptowane',
    textColor: '#047857',
    backgroundColor: '#d1fae5',
  },
  [STATUS_SAVE_TO_TMS]: {
    label: 'Do zapisu w TMS',
    textColor: '#b45309',
    backgroundColor: '#fef3c7',
  },
  [STATUS_IN_TMS]: {
    label: 'W TMS',
    textColor: '#1d4ed8',
    backgroundColor: '#dbeafe',
  },
  [STATUS_FAILED_PUSH_TMS]: {
    label: 'Błąd zapisu',
    textColor: '#b91c1c',
    backgroundColor: '#fee2e2',
  },
};

/** Wygląd stanu spoza zestawu. **Nigdy nie udajemy, że zlecenie jest w TMS.** */
export const UNKNOWN_STATUS_APPEARANCE: AcceptanceStatusAppearance = {
  label: 'Stan nieznany',
  textColor: '#6b7280',
  backgroundColor: '#f3f4f6',
};

export function isKnownAcceptanceStatus(value: unknown): value is AcceptanceStatus {
  return typeof value === 'string' && (ALL_ACCEPTANCE_STATUSES as readonly string[]).includes(value);
}

/**
 * Wygląd dla wartości z rekordu.
 *
 * W FreightFusion widok szczegółów miał tu fallback zwracający „W TMS" dla
 * wszystkiego, czego nie rozpoznał. Zlecenie dopiero zakolejkowane pokazywało
 * się jako zapisane u klienta. Tutaj nieznana wartość jest nieznana.
 */
export function acceptanceStatusAppearance(value: unknown): AcceptanceStatusAppearance {
  return isKnownAcceptanceStatus(value) ? STATUS_APPEARANCE[value] : UNKNOWN_STATUS_APPEARANCE;
}

/** Czy stan wymaga reakcji człowieka — podświetlenie na liście. */
export function needsAttention(value: unknown): boolean {
  return value === STATUS_FAILED_PUSH_TMS;
}
